/**
 * One row of a dialog's form, laid out as the Branches dialog and the Imports
 * dialog are (C14 §10.6 item 7): the label in a column of its own beside the
 * control from `sm` up, stacked above it on a phone. Shared by every staff
 * dialog since the Wave 15 close; it lived in `routes/Cases` until then.
 *
 * The control is the kit's own (`Input`, `Select`, `Textarea`) given the `id`
 * this row's label points at, and no `label` of its own — so the label is a
 * real `<label>` and the kit still draws the hint and the error under the
 * control.
 *
 * `required` draws the red star (C27 item 11, UAT report #19). The star is
 * presentation only — `aria-hidden`, because the control itself carries
 * `required` / `aria-required`, which is what a screen reader announces.
 */

import type { ReactNode } from 'react'

/** The grid a dialog's rows sit in. On a phone the label sits just above its
 *  control and the rows keep their distance from each other (the control's
 *  own bottom margin); from `sm` up the grid's gap spaces the rows. */
export const FORM_GRID =
  'grid gap-x-4 gap-y-1.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-start sm:gap-y-4'

export function FormRow({
  label,
  htmlFor,
  optional = false,
  required = false,
  children,
}: {
  label: ReactNode
  /** The control's `id`. Omit for a row whose control is a group. */
  htmlFor?: string
  optional?: boolean
  required?: boolean
  children: ReactNode
}) {
  const text = (
    <>
      {label}
      {required && (
        <span className="text-destructive" aria-hidden="true" data-required-star>
          {' *'}
        </span>
      )}
      {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
    </>
  )
  return (
    <>
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-foreground text-sm font-medium sm:pt-3">
          {text}
        </label>
      ) : (
        <span className="text-foreground text-sm font-medium sm:pt-3">{text}</span>
      )}
      <div className="mb-3 min-w-0 last:mb-0 sm:mb-0">{children}</div>
    </>
  )
}

/** The footer every dialog here ends with: Cancel, then the one action. */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{children}</div>
}
